'use client';

import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef } from 'react';

import { useBatchReplyV3Status } from '@/api-hooks/useBatchReplyV3';
import { TextShimmer } from '@/components/v4/TextShimmer';
import { findCachedReview } from '@/lib/batchReplyV3Cache';

import { batchProgressTitle } from './progressLabel';

import {
  type IslandEvent,
  useBatchReplyV3JobIdValue,
  useCurrentIslandMessageValue,
  useIslandQueue,
  useSetCurrentIslandMessage,
} from './context';

// 큐 소비 페이스 — 백로그가 많으면 빨리 따라잡되, 평상시엔 "탁·탁·탁" 하나씩
const IDLE_KEY = -1;
const REVEAL_BASE_MS = 1400;
const TITLE_REVEAL_MS = 1100; // 아이템 사이에 끼워 넣는 진행률 타이틀 노출 시간
const revealDelay = (backlog: number) => (backlog > 8 ? 480 : backlog > 3 ? 820 : REVEAL_BASE_MS);

const buildText = (queryClient: QueryClient, ev: IslandEvent): string => {
  const review = findCachedReview(queryClient, ev.reviewId);
  const name = review?.reviewer?.trim();
  const menu = review?.menus?.[0];
  const label = name ? `${name}님 리뷰` : menu ? `${menu} 리뷰` : '리뷰';
  return ev.state === 'completed' ? `${label}에 댓글을 등록했어요` : `${label} 등록에 실패했어요`;
};

/**
 * 진행 피드 단일 드라이버 — island 큐를 stagger로 소비해 현재 메시지를 atom(context)에 기록.
 * ⚠️ 큐는 dequeue(단일 소비자)이므로 앱 전체에 1개만 마운트할 것(레이아웃 루트).
 * 위젯/배너는 IslandFeedText로 atom을 "읽기만" 한다.
 * (mock이면 useBatchReplyV3Status는 내부 게이트로 fetch 없이 seed된 캐시만 읽어 프리뷰에서도 동작)
 */
export const IslandFeedDriver = () => {
  const jobId = useBatchReplyV3JobIdValue();
  const queryClient = useQueryClient();
  const [queue, setQueue] = useIslandQueue();
  const setCurrent = useSetCurrentIslandMessage();
  const { data: status } = useBatchReplyV3Status(jobId ?? null);
  const isRunning = status?.status === 'IN_PROGRESS' || status?.status === 'ACTIVE';

  const queueRef = useRef(queue);
  queueRef.current = queue;
  const runningRef = useRef(isRunning);
  runningRef.current = isRunning;

  // 피드 아이템("xxx님...") 사이 idle 자리에 진행률 기반 타이틀(시작/중/거의완료)을 노출.
  const total = status?.stats?.total ?? 0;
  const processed = (status?.stats?.completed ?? 0) + (status?.stats?.failed ?? 0);
  const idleTitle = batchProgressTitle(total > 0 ? (processed / total) * 100 : 0, false, false);
  const idleTitleRef = useRef(idleTitle);
  idleTitleRef.current = idleTitle;

  // 큐 드레인 루프 (recursive setTimeout — backlog에 따라 가변 딜레이)
  useEffect(() => {
    if (!jobId) {
      setCurrent(null);
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    // 아이템("xxx님...")과 진행률 타이틀을 번갈아 노출 → 큐가 꽉 차 있어도 타이틀이 "사이사이" 보인다.
    let showTitleNext = false;
    const setIdleTitle = () =>
      setCurrent((cur) =>
        cur && cur.state === 'idle' && cur.text === idleTitleRef.current
          ? cur
          : { key: IDLE_KEY, text: idleTitleRef.current, state: 'idle' },
      );
    const step = () => {
      const q = queueRef.current;
      // 직전에 아이템을 보여줬으면 이번엔 진행률 타이틀 한 번 끼워 넣기
      if (showTitleNext && runningRef.current) {
        showTitleNext = false;
        setIdleTitle();
        timer = setTimeout(step, TITLE_REVEAL_MS);
        return;
      }
      if (q.length > 0) {
        const head = q[0];
        setQueue((prev) => prev.slice(1));
        setCurrent({ key: head.key, text: buildText(queryClient, head), state: head.state });
        showTitleNext = true; // 다음 스텝은 진행률 타이틀
        timer = setTimeout(step, revealDelay(q.length));
        return;
      }
      // 큐 비었고 진행 중 → 진행률 타이틀 유지(중→거의완료 갱신)
      if (runningRef.current) setIdleTitle();
      timer = setTimeout(step, 700);
    };
    timer = setTimeout(step, 250);
    return () => clearTimeout(timer);
  }, [jobId, queryClient, setQueue, setCurrent]);

  return null;
};

/**
 * 진행 피드 1줄 — 기존 타이틀 자리에 그대로 대체(새 줄 추가 X).
 * V3ProgressIsland와 동일한 슬라이드 스왑(AnimatePresence) + shimmer(TextShimmer).
 * atom을 읽기만 하므로 위젯/배너 여러 곳에서 안전하게 사용.
 */
export const IslandFeedText = ({
  className,
  fallback,
}: {
  /** shimmer 색/폰트 (예: 'text-[16px] font-semibold [--base-color:#35373E] [--base-gradient-color:#EA5400]') */
  className?: string;
  /** 아직 메시지가 없을 때 표시할 기본 텍스트 */
  fallback?: string;
}) => {
  const current = useCurrentIslandMessageValue();
  const text = current?.text ?? fallback ?? '';
  const key = current?.key ?? 'fallback';

  return (
    <span className="relative block overflow-hidden">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={key}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="block"
        >
          <TextShimmer duration={1.3} className={className}>
            {text}
          </TextShimmer>
        </motion.span>
      </AnimatePresence>
    </span>
  );
};
